/**
 * P.ts
 * @author Diao Zheng
 * @file Boolean logic with functions
 */
// @barrel export all
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import uniq from "lodash/uniq";
import * as F from "./F";
import * as Option from "./Option";
const NEGATE = Symbol("nasi/NEGATE");
const CONJUNCTION = Symbol("nasi/CONJUNCTION");
const DISJUNCTION = Symbol("nasi/DISJUNCTION");
function isNegatable(p) {
    return p.hasOwnProperty(NEGATE);
}
function isConjunction(p) {
    return p.hasOwnProperty(CONJUNCTION);
}
function isDisjunction(p) {
    return p.hasOwnProperty(DISJUNCTION);
}
export function never(_pred) {
    return false;
}
export function always(_pred) {
    return true;
}
export function is(obj) {
    return (other) => Object.is(other, obj);
}
export function eq(obj) {
    return (other) => obj === other;
}
export function matches(regex) {
    return (s) => regex.test(s);
}
function boundConjunctionExecutor(predicates, arg) {
    for (const predicate of predicates) {
        if (!predicate(arg)) {
            return false;
        }
    }
    return true;
}
function boundDisjunctionExecutor(predicates, arg) {
    for (const predicate of predicates) {
        if (predicate(arg)) {
            return true;
        }
    }
    return false;
}
export function and(...predicates) {
    const filtered = [];
    for (const predicate of predicates) {
        if (predicate === never) {
            return never;
        }
        else if (predicate === always) {
            continue;
        }
        else if (isConjunction(predicate)) {
            filtered.push(...predicate[CONJUNCTION]);
        }
        else {
            filtered.push(predicate);
        }
    }
    const conjunction = boundConjunctionExecutor.bind(undefined, uniq(filtered));
    conjunction[CONJUNCTION] = filtered;
    return conjunction;
}
export function or(...predicates) {
    const filtered = [];
    for (const predicate of predicates) {
        if (predicate === always) {
            return always;
        }
        else if (predicate === never) {
            continue;
        }
        else if (isDisjunction(predicate)) {
            filtered.push(...predicate[DISJUNCTION]);
        }
        else {
            filtered.push(predicate);
        }
    }
    const disjunction = boundDisjunctionExecutor.bind(undefined, uniq(filtered));
    disjunction[DISJUNCTION] = filtered;
    return disjunction;
}
export function not(predicate) {
    if (isNegatable(predicate)) {
        return predicate[NEGATE];
    }
    // see if it's a conjunction or disjunction
    if (isConjunction(predicate)) {
        const disjunction = or(...predicate[CONJUNCTION].map(not));
        disjunction[NEGATE] = predicate;
        predicate[NEGATE] = disjunction;
        return disjunction;
    }
    else if (isDisjunction(predicate)) {
        const conjunction = and(...predicate[DISJUNCTION].map(not));
        conjunction[NEGATE] = predicate;
        predicate[NEGATE] = conjunction;
        return conjunction;
    }
    const negate = (arg) => !predicate(arg);
    negate[NEGATE] = predicate;
    predicate[NEGATE] = negate;
    return negate;
}
export function infer(precedant, antecedent) {
    return or(not(precedant), antecedent);
}
export function branch(predicate, ifTrue, ifFalse) {
    return (arg) => {
        if (predicate(arg)) {
            return ifTrue(arg);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            return ifFalse(arg);
        }
    };
}
export function exists(key) {
    return (obj) => Option.isSome(Option.property(obj, key));
}
export function existsWith(key, predicate) {
    return (obj) => Option.mapChoice(Option.property(obj, key), predicate, false);
}
export function map(mapper, pred) {
    return F.compose(pred, mapper);
}
export function pred(predicate) {
    return F.memoise(predicate);
}
// we set never and always's negations to one another
never[NEGATE] =
    always;
always[NEGATE] =
    never;
